import {css, html, nothing} from "@isceco/widget-library2/external/lit";
import FormComponent from "../../../components/FormComponent";
import EformSubmitStatus from "../../../components/SubmitStatus/EformSubmitStatus";
import NachtragService from "./NachtragService";
import '../../../components/InlineNotification'

export default class Nachtrag extends FormComponent {

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.refId = this._getQueryParam('refid');
    this.initialized.then(() => {
      this.reload()
    })
  }

  get translationFile() {
    return './views/WBF/Nachtrag/i18n.json'
  }

  get css() {
    return css`
      .form-elements {
        display: flex;
        flex-direction: column;
        gap: var(--isceco-space-300);
      }

      ul {
        padding-top: 0.25rem;
      }

      li {
        list-style: disc;
        padding: 0.25rem 0;
      }
    `
  }

  getTemplate() {
    const refIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
    const refIdIsMissing = this.refId == null;
    const refIdIsMalformed = this.refId !== null && !refIdPattern.test(this.refId);
    const refIdIsValid = this.refId != null && refIdPattern.test(this.refId);
    return html`
      <isceco-title id="page-title"
                    text="${this.i18n.translate('page.title')}"
      ></isceco-title>
      ${refIdIsMissing ? this._renderBannerRefIdMissing() : nothing}
      ${refIdIsMalformed ? this._renderBannerRefIdInvalid() : nothing}
      ${refIdIsValid ? this._renderForm() : nothing}
    `
  }

  _renderBannerRefIdMissing() {
    return html`
      <inline-notification variant="danger">
        <span>${this.i18n.translate('form.reference.missing')}</span>
      </inline-notification>
    `
  }

  _renderBannerRefIdInvalid() {
    return html`
      <inline-notification variant="danger">
        <span>${this.i18n.translate('form.reference.invalid')}</span>
      </inline-notification>
    `
  }

  _renderForm() {
    const submitKey = this.preview ? 'form.button.submit' : 'form.button.preview'
    return html`
      <eform-submit-status></eform-submit-status>
      <isceco-form id="nachtrag-form"
                   include-disabled
                   submit-button-text="${this.i18n.translate(submitKey)}"
                   @submit="${e => this._submit(e)}">
        <div slot="buttons">
          <isceco-button text="${this.i18n.translate('form.button.abort')}"
                         @click="${() => this._abort()}"
                         variant="secondary"
                         .disabled="${!this.preview}"
          ></isceco-button>
        </div>
        <div slot="form-elements" class="form-elements">
          ${this._getFormElements()}
        </div>
      </isceco-form>
    `
  }

  _submit(event) {
    if (!this.preview) {
      this.preview = true
      this.reload()
      return;
    }

    new NachtragService().submit(event.detail, event.target)
  }

  _abort() {
    if (this.preview) {
      this.preview = false
      send(EformSubmitStatus.EVENT.CLOSE, {})
      this.render()
    }
  }

  _getFormElements = () => {
    return html`
      ${this._renderInfoBanner()}
      <isceco-card>
        ${this._renderHiddenRefIdInput()}
        <div style="display: flex; flex-direction: column; gap: var(--isceco-space-300)">
          ${this._renderEmail()}
          ${this._renderDescriptionInput()}
          ${this._renderDocumentUpload()}
          ${this._renderNote()}
        </div>
      </isceco-card>
    `
  }

  _renderInfoBanner() {
    return html`
      <inline-notification variant="info">
        <span>${this.i18n.translate('page.info.email')}</span>
      </inline-notification>
      <inline-notification variant="info">
        <div>
          ${this.i18n.translate('page.info.fields')}
          <ul>
            <li>${this.i18n.translate('form.description')}</li>
            <li>${this.i18n.translate('form.documents')}</li>
          </ul>
        </div>
      </inline-notification>
    `
  }

  _renderHiddenRefIdInput() {
    return html`
      <isceco-text-input
        name="referenceId"
        value="${this.refId}"
        type="hidden">
      </isceco-text-input>
    `
  }

  _renderEmail() {
    return html`
      <isceco-text-input
        name="email"
        label="${this.i18n.translate('form.email')}"
        type="email"
        pattern-error-text="${this.i18n.translate('form.email.error')}"
        .readonly="${this.preview}"
        required
      ></isceco-text-input>
    `
  }

  _validate() {
    const attachments = document.querySelector('[name="attachments"]');
    return attachments?.getValue() != null
  }

  _renderDescriptionInput() {
    return html`
      <isceco-textarea
        name="description"
        label="${this.i18n.translate('form.description')}"
        placeholder="${this.i18n.translate('form.description.placeholder')}"
        maxlength="1000"
        resize="manual"
        rows="4"
        .readonly="${this.preview}"
        .required="${this._validate()}"
      ></isceco-textarea>
    `
  }

  _renderDocumentUpload() {
    return html`
      <isceco-file-input
        label="${this.i18n.translate('form.documents')}"
        name="attachments"
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        multiple
        maxfiles="25"
        maxsize="${this.fileMaxSize}"
        .fileInputService="${this.fileInputService}"
        .disabled="${this.preview}"
      ></isceco-file-input>
    `
  }

  _renderNote() {
    return html`
      <isceco-textarea
        name="note"
        label="${this.i18n.translate('form.note')}"
        placeholder="${this.i18n.translate('form.note.placeholder')}"
        pattern="^(.{1,999}).*"
        maxlength="1000"
        rows="4"
        .readonly="${this.preview}"
        resize="manual"
      ></isceco-textarea>
    `
  }

  _getQueryParam(param) {
    // We have to do this weird splitting because the '#' in the path disrupts the URLSearchParams logic
    const params = window.location.href.split('?')[1];
    return param != null ? new URLSearchParams(params?.toLowerCase()).get(param) : null;
  }
}
customElements.define('wbf-nachtrag', Nachtrag)
